<template>
  <div class="flex flex-col lg:flex-row justify-between gap-6">
    <div class="lg:w-5/12 space-y-6">
      <!-- Type Selection -->
      <div>
        <label class="block font-medium text-sm text-gray-700 mb-2">
          Type
        </label>
        <div class="flex space-x-2">
          <div class="py-2 px-3 border-2 border-blue-500 bg-blue-50 rounded text-blue-600 cursor-pointer font-semibold">
            <font-awesome-icon icon="link"/> URL
          </div>
          <div class="py-2 px-3 border-2 border-gray-300 rounded text-gray-400 cursor-not-allowed">
            <font-awesome-icon icon="address-card"/> Contact card
          </div>
          <div class="py-2 px-3 border-2 border-gray-300 rounded text-gray-400 cursor-not-allowed">
            <font-awesome-icon icon="list"/> Link page
          </div>
          <input v-model="data.type" id="type" name="type" value="redirect" hidden>
        </div>
      </div>

      <!-- Form -->
      <form @submit.prevent="update" class="space-y-4">
        <!-- Name -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="name">
            Naam
          </label>
          <input v-model="data.name"
                 class="p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200"
                 type="text"
                 id="name"
                 required>
        </div>

        <!-- Target URL -->
        <div>
          <label class="block font-medium text-sm text-gray-700 mb-2" for="url">
            Redirect (naar)
          </label>
          <input v-model="data.target_url"
                 class="p-3 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200"
                 type="url"
                 id="url"
                 required
                 placeholder="https://"
                 @input="handleInput">
        </div>

        <!-- QR Styling Section -->
        <div class="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h4 class="font-semibold text-gray-900 mb-3">QR Code Opties</h4>

          <div class="space-y-3">
            <!-- Size -->
            <div>
              <label class="block font-medium text-sm text-gray-700 mb-2">
                Grootte: {{ data.qr_size }}px
              </label>
              <input v-model="data.qr_size"
                     type="range"
                     min="100"
                     max="1000"
                     step="50"
                     class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
              <div class="flex justify-between text-xs text-gray-600 mt-1">
                <span>100px</span>
                <span>1000px</span>
              </div>
            </div>

            <!-- Style -->
            <div>
              <label class="block font-medium text-sm text-gray-700 mb-2">
                Blok Stijl
              </label>
              <select v-model="data.qr_style"
                      class="p-2 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200">
                <option value="square">Vierkant</option>
                <option value="dot">Dots</option>
                <option value="round">Rond</option>
              </select>
            </div>

            <!-- Eye Style -->
            <div>
              <label class="block font-medium text-sm text-gray-700 mb-2">
                Hoek Stijl
              </label>
              <select v-model="data.qr_eye_style"
                      class="p-2 rounded-md shadow-sm bg-white border border-gray-300 text-gray-900 block w-full focus:border-blue-500 focus:ring focus:ring-blue-200">
                <option value="square">Vierkant</option>
                <option value="circle">Cirkel</option>
              </select>
            </div>

            <!-- Foreground Color -->
            <div>
              <label class="block font-medium text-sm text-gray-700 mb-2">
                Voorgrond Kleur
              </label>
              <color-picker v-model:pureColor="foregroundColor"/>
            </div>

            <!-- Background Color -->
            <div>
              <label class="block font-medium text-sm text-gray-700 mb-2">
                Achtergrond Kleur
              </label>
              <color-picker v-model:pureColor="pureColor"/>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <div v-if="errors && errors.message" class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {{ errors.message }}
        </div>

        <!-- Submit Button -->
        <div class="flex items-center justify-end">
          <button type="submit"
                  :disabled="loading"
                  class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring ring-blue-300 disabled:opacity-50 transition">
            {{ loading ? 'Bezig...' : 'Aanmaken' }}
          </button>
        </div>
      </form>
    </div>

    <!-- QR Code Preview -->
    <div class="lg:w-5/12 flex flex-col justify-center items-center">
      <div class="bg-gray-100 p-6 rounded-lg border-2 border-gray-300">
        <div class="w-[400px] h-[400px] bg-white rounded flex items-center justify-center">
          <img v-if="qrCode" :src="'data:image/png;base64,' + qrCode" alt="QR Code Preview" class="max-w-full max-h-full" />
          <div v-else class="text-gray-400 text-center">
            <svg class="w-20 h-20 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm2 2V5h1v1H5zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zm2 2v-1h1v1H5zM13 3a1 1 0 00-1 1v3a1 1 0 001 1h3a1 1 0 001-1V4a1 1 0 00-1-1h-3zm1 2v1h1V5h-1z" clip-rule="evenodd"></path>
              <path d="M11 4a1 1 0 10-2 0v1a1 1 0 002 0V4zM10 7a1 1 0 011 1v1h2a1 1 0 110 2h-3a1 1 0 01-1-1V8a1 1 0 011-1zM16 9a1 1 0 100 2 1 1 0 000-2zM9 13a1 1 0 011-1h1a1 1 0 110 2v2a1 1 0 11-2 0v-3zM7 11a1 1 0 10-2 0v4a1 1 0 102 0v-4zM15 11a1 1 0 10-2 0v4a1 1 0 102 0v-4z"></path>
            </svg>
            <p class="text-sm">QR code preview</p>
          </div>
        </div>
        <div class="mt-4 flex items-center justify-between">
          <p class="text-sm text-gray-600">Live preview</p>
          <button
            v-if="qrCode"
            @click="downloadQRCode"
            type="button"
            class="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-semibold text-xs uppercase tracking-widest transition">
            <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { mapActions } from 'pinia';
import { useAuthStore } from '@/stores/auth.js';
import { useTagsStore } from '@/stores/tags.js';
import { toast } from 'vue3-toastify';
import { ColorPicker } from "vue3-colorpicker";
import "vue3-colorpicker/style.css";
import axios from 'axios';

export default {
  components: { ColorPicker },
  setup() {
    const pureColor = ref("#ffffff");
    const foregroundColor = ref("#000000");
    const data = ref({
      name: null,
      type: 'redirect',
      target_url: 'https://',
      qr_size: 500,
      qr_style: 'square',
      qr_eye_style: 'square',
      qr_color: '0,0,0',
      qr_bg_color: '255,255,255',
    });
    const qrCode = ref(null);
    const errors = ref({});
    const loading = ref(false);
    let debounceTimer = null;

    // Parse color to RGB
    const parseRGB = (color) => {
      let rgb = [255, 255, 255];
      if (color.startsWith('#')) {
        rgb = color.match(/\w\w/g).map(x => parseInt(x, 16));
      } else if (color.startsWith('rgb')) {
        rgb = color.match(/\d+/g).map(Number);
      }
      return rgb;
    };

    const hexToRgbString = (hex) => {
      const rgb = parseRGB(hex);
      return `${rgb[0]},${rgb[1]},${rgb[2]}`;
    };

    // Debounced API call for QR preview
    const updateQRCode = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(async () => {
        if (!data.value.target_url || data.value.target_url === 'https://') return;

        try {
          const bgRgb = parseRGB(pureColor.value);
          const fgRgb = parseRGB(foregroundColor.value);

          const response = await axios.post('/api/generate-qr', {
            data: data.value.target_url,
            size: data.value.qr_size,
            margin: 1,
            color: fgRgb,
            bgColor: bgRgb,
            style: data.value.qr_style,
            eyeStyle: data.value.qr_eye_style,
          });
          qrCode.value = response.data.qrCode;
        } catch (error) {
          console.error('Error generating QR code:', error);
        }
      }, 500);
    };

    // Watchers
    watch(pureColor, (newVal) => {
      data.value.qr_bg_color = hexToRgbString(newVal);
      updateQRCode();
    });

    watch(foregroundColor, (newVal) => {
      data.value.qr_color = hexToRgbString(newVal);
      updateQRCode();
    });

    watch(() => data.value.target_url, updateQRCode);
    watch(() => data.value.qr_size, updateQRCode);
    watch(() => data.value.qr_style, updateQRCode);
    watch(() => data.value.qr_eye_style, updateQRCode);

    return { pureColor, foregroundColor, data, qrCode, errors, loading };
  },
  methods: {
    ...mapActions(useAuthStore, ['attempt_user']),
    handleInput(event) {
      this.data.target_url = event.target.value;
    },
    downloadQRCode() {
      if (!this.qrCode) return;

      const fileName = this.data.name ? `${this.data.name.replace(/[^a-z0-9]/gi, '_')}_qr.png` : 'qr_code.png';
      const link = document.createElement('a');
      link.href = 'data:image/png;base64,' + this.qrCode;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('QR code gedownload!', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    },
    update() {
      this.loading = true;
      this.errors = {};

      axios.post('/api/tags', this.data)
        .then((response) => {
          const tagsStore = useTagsStore();
          tagsStore.invalidateCache();

          this.attempt_user();
          this.loading = false;
          this.$router.push({ name: 'Tags' });

          setTimeout(() => {
            toast.success('Tag aangemaakt!', {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }, 50);
        })
        .catch((error) => {
          this.errors = error.response?.data || { message: 'Er is een fout opgetreden.' };
          this.loading = false;
        });
    }
  }
};
</script>
